import {
    AlertModal,
    Box,
    Button,
    Checkbox,
    Export,
    Heading,
    Item,
    Modal,
    Select,
    showToast,
    TextBlock,
} from "@bcc-ui/ui-components";
import { useFeature } from "flagged";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { t } from "i18next";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import style from "./user-settings-form.scss";
import {
    ASSET_MANAGEMENT_ROUTE,
    DASHBOARD_ROUTE,
    DELIVERYHISTORY_LICENSES_ROUTE,
    HOME_ROUTE,
    INTEGRATIONS_ROUTE,
    LICENSES_ROUTE,
    REPORTS_ROUTE,
    RouteDefinition,
    SUPPORT_AND_HELP_ROUTE,
    SUSTAINABILITY_DASHBOARD_ROUTE,
    TENANTS_ROUTE,
    USERS_ROUTE,
    WORKFLOWS_ROUTE,
} from "components/router/Routes";
import { AUTH_LICENSE_ASSIGN, AUTH_LICENSE_VIEW, AUTH_TENANT_ACCESS } from "domain/authority";
import { Breakpoint } from "domain/breakpoints";
import { UserDetails } from "domain/user";
import { FLAG_ASSET_MANAGEMENT } from "services/feature/FeatureFlagService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userService } from "services/user/users/UserService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { setUser } from "store/user";
import { createDateLabel } from "utils/format";

import testIds from "testIds.json";

interface Props {
    setIsSubmitting: (isSubmitting: boolean) => void;
    setIsDirty: (isDirty: boolean) => void;
}

export interface FormValues {
    userDefaultView: string;
    tenantAccessDefaultView: string;
    productNewsEmailAlerts: boolean;
}

export const USER_SETTINGS_FORM_ID = "user-settings-form";
const EULA_PDF_URL = "/public/eula/eula.pdf";

interface FormStateSyncProps {
    dirty: boolean;
    setIsDirty: (isDirty: boolean) => void;
}

// Sync form state with parent component via callbacks
const FormStateSync = (props: FormStateSyncProps) => {
    React.useEffect(() => props.setIsDirty(props.dirty), [props.dirty, props.setIsDirty]);
    return null;
};

export const UserSettingsForm = (props: Props) => {
    const dispatch = useDispatch();
    const user = useSelector((state: StoreState) => state.userReducer.user);
    const tenantDetails = useSelector(
        (state: StoreState) => state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1]
    );
    const eulaAcceptedDate = user?.eulaAcceptedDate ? createDateLabel(user.eulaAcceptedDate) : "";
    const [isTOSModalVisible, setIsTOSModalVisible] = React.useState(false);
    const [isAlertModalVisible, setAlertModalVisible] = React.useState<boolean>(false);

    const iframeRef = React.useRef<HTMLIFrameElement>(null);
    const isMobileResolution = window.innerWidth < Breakpoint.MD;

    const initialValues: FormValues = {
        userDefaultView: user?.default_settings ? user.default_settings.default_view : HOME_ROUTE.path,
        tenantAccessDefaultView: user?.default_settings
            ? user.default_settings.tenant_access_default_view
            : userSessionService.userHasAllAuthorities([AUTH_LICENSE_VIEW, AUTH_LICENSE_ASSIGN])
              ? DELIVERYHISTORY_LICENSES_ROUTE.path
              : HOME_ROUTE.path,

        productNewsEmailAlerts: user?.default_settings.news_email_opt_in ?? false,
    };

    const createDefaultViewSelection = (additionalRoutes: RouteDefinition[]) => {
        const routes = [
            HOME_ROUTE,
            DASHBOARD_ROUTE,
            REPORTS_ROUTE,
            SUSTAINABILITY_DASHBOARD_ROUTE,
            LICENSES_ROUTE,
            DELIVERYHISTORY_LICENSES_ROUTE,
            USERS_ROUTE,
            WORKFLOWS_ROUTE,
            SUPPORT_AND_HELP_ROUTE,
            ...additionalRoutes,
        ];

        if (useFeature(FLAG_ASSET_MANAGEMENT)) {
            routes.push(ASSET_MANAGEMENT_ROUTE);
            routes.push(INTEGRATIONS_ROUTE);
        }

        routes.sort((routeA, routeB) => t(routeA.title).localeCompare(t(routeB.title)));

        const items: Item[] = [];

        routes.forEach((route) => {
            route.isValidUser(user) &&
                route.isAccessibleTo(tenantDetails) &&
                items.push({ label: t(route.title), value: route.path });
        });

        return items;
    };

    const submitSettings = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
        if (!user) {
            return;
        }

        props.setIsSubmitting(true);
        const abortController = new AbortController();
        const { signal } = abortController;

        try {
            const defaultSettings = {
                default_view: values.userDefaultView,
                tenant_access_default_view:
                    userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                    userSessionService.userHasAllAuthorities([AUTH_TENANT_ACCESS])
                        ? values.tenantAccessDefaultView
                        : "",
                news_email_opt_in: values.productNewsEmailAlerts,
            };
            await userService
                .editDefaultViewSettings(user.uuid, defaultSettings, abortController)
                .then(() => {
                    const userDetails: UserDetails = { ...user, default_settings: defaultSettings };
                    userSessionService.storeUser(userDetails);
                    dispatch(setUser(userDetails));
                    helpers.resetForm({ values });
                    showToast(t("Settings.successMessage"), "success");
                })
                .finally(() => {
                    props.setIsSubmitting(false);
                });
        } catch {
            if (!signal.aborted) {
                props.setIsSubmitting(false);
                setAlertModalVisible(true);
            }
        }
    };

    return (
        <>
            <Formik<FormValues> initialValues={initialValues} onSubmit={submitSettings}>
                {({ values, dirty, isSubmitting, setFieldValue }: FormikProps<FormValues>) => {
                    return (
                        <Form id={USER_SETTINGS_FORM_ID} className={style.formContent}>
                            <FormStateSync dirty={dirty} setIsDirty={props.setIsDirty} />
                            <Box columnLayout gap="Lg">
                                <Box columnLayout>
                                    <Heading variant="h2" underline>
                                        {t("Settings.termsAndConditionsTitle")}
                                    </Heading>

                                    <Box gap="Xs" paddingBottom="Xs" columnLayout={isMobileResolution}>
                                        <TextBlock disableBottomSpacing>
                                            <strong>{t("Settings.termsAndConditions")}</strong>
                                        </TextBlock>
                                        <TextBlock
                                            disableBottomSpacing
                                            testId={testIds.workArea.settings.my.tos.acceptedDate}
                                        >
                                            {t("Settings.acceptedDate")} {eulaAcceptedDate}
                                        </TextBlock>
                                    </Box>

                                    <Button
                                        variant={"Tertiary"}
                                        type="button"
                                        onClick={() => {
                                            setIsTOSModalVisible(true);
                                            usageStatisticsService.sendEvent({
                                                category: Category.HEADER,
                                                action: Action.VIEW_TERMS_AND_CONDITIONS,
                                            });
                                        }}
                                        disabled={isSubmitting}
                                        testId={testIds.workArea.settings.my.tos.termsAndConditionsLink}
                                    >
                                        {t("Settings.viewTermsAndConditions")}
                                    </Button>
                                </Box>

                                <Box columnLayout>
                                    <Heading tag="div" variant="h2" underline>
                                        {t("Settings.defaultSettings")}
                                    </Heading>

                                    <Box columnLayout gap="Sm">
                                        <Select
                                            name="user_default_view"
                                            label={t("Settings.defaultView")}
                                            labelAbove={isMobileResolution}
                                            placeholder={t("Settings.selectDefaultView")}
                                            selectedByDefault={values.userDefaultView}
                                            items={createDefaultViewSelection([TENANTS_ROUTE])}
                                            onSelection={(value) => {
                                                setFieldValue("userDefaultView", value);
                                            }}
                                            disabled={isSubmitting}
                                            width={220}
                                            dropdownMaxHeight={200}
                                            testId={testIds.workArea.settings.my.userDefaultViewSelect}
                                        />

                                        {userSessionService.hasFeatureLicense("FEATURE_TENANT_MANAGEMENT") &&
                                            userSessionService.userHasAllAuthorities([AUTH_TENANT_ACCESS]) && (
                                                <Select
                                                    name="tenant_access_default_view"
                                                    label={t("Settings.customerAccessDefaultView")}
                                                    labelAbove={isMobileResolution}
                                                    placeholder={t("Settings.selectDefaultView")}
                                                    selectedByDefault={values.tenantAccessDefaultView}
                                                    items={createDefaultViewSelection([])}
                                                    onSelection={(value) => {
                                                        setFieldValue("tenantAccessDefaultView", value);
                                                    }}
                                                    disabled={isSubmitting}
                                                    width={220}
                                                    dropdownMaxHeight={200}
                                                    testId={testIds.workArea.settings.my.tenantAccessDefaultViewSelect}
                                                />
                                            )}
                                    </Box>
                                </Box>

                                <Box columnLayout>
                                    <Heading variant="h2" underline>
                                        {t("Settings.newsEmailAlertTitle")}
                                    </Heading>

                                    <Box gap="Xs">
                                        <TextBlock disableBottomSpacing>{t("Settings.newsEmailAlert")}</TextBlock>
                                        <Checkbox
                                            name={"product_news"}
                                            checked={values.productNewsEmailAlerts}
                                            onChange={(e) => setFieldValue("productNewsEmailAlerts", e.target.checked)}
                                            disabled={isSubmitting}
                                            testId={testIds.workArea.settings.my.newsEmailAlertsCheckbox}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Form>
                    );
                }}
            </Formik>

            <Modal
                title={t("Login.acceptEulaModal.title")}
                isOpen={isTOSModalVisible}
                hideModal={() => setIsTOSModalVisible(false)}
                className={style.tosModal}
            >
                <Box columnLayout paddingBottom={"Md"} className={style.tosModalWrapper}>
                    <Box gap="Xxs" horizontalAlignment="Right" verticalAlignment="Center" paddingBottom="Xs">
                        <Button
                            variant="Tertiary"
                            type="button"
                            icon={<Export />}
                            onClick={() => {
                                window.open(EULA_PDF_URL, "_blank", "noopener");
                                usageStatisticsService.sendEvent({
                                    category: Category.HEADER,
                                    action: Action.DOWNLOAD_TERMS_AND_CONDITIONS,
                                });
                            }}
                            testId={testIds.workArea.settings.my.tos.downloadPdfLink}
                        >
                            {t("Login.acceptEulaModal.downloadPdf")}
                        </Button>
                    </Box>

                    <iframe
                        ref={iframeRef}
                        src="/public/eula/eula.html"
                        className={style.tosModalIframe}
                        data-testid={testIds.login.eula.contentIframe}
                    />
                </Box>
            </Modal>

            <AlertModal
                title={t("Settings.errorTitle")}
                variant={"Error"}
                isOpen={isAlertModalVisible}
                buttons={
                    <Button
                        variant="Primary"
                        onClick={() => setAlertModalVisible(false)}
                        testId={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </Button>
                }
                hideModal={() => setAlertModalVisible(false)}
            >
                {t("Settings.errorMessage")}
            </AlertModal>
        </>
    );
};
